import { defineStore } from "pinia";

export const useEventStore = defineStore("events", {
  state: () => ({
    events: [],
    categories: [],
    tags: [],
    eventTypes: [],
    recommendedEvents: [],
    loading: false,
    error: null,
    pagination: {
      page: 1,
      pageSize: 25,
      pageCount: 1,
      total: 0,
    },
  }),

  actions: {
    async getEvents(params: {
      page?: number;
      pageSize?: number;
      categories?: number[];
      tags?: number[];
      types?: number[]; // Add types parameter
      featured?: boolean;
      streams?: boolean;
      sort?: string;
      search?: string;
      date?: { from: string; to: string };
    }) {
      try {
        this.loading = true;
        const { find } = useStrapi();

        // Build filters
        const filters: any = {};
        if (params.categories?.length) {
          filters.aa_category = { id: { $in: params.categories } };
        }
        if (params.tags?.length) {
          filters.aa_tags = { id: { $in: params.tags } };
        }
        if (params.types?.length) {
          filters.aa_events_type = { id: { $in: params.types } };
        }
        if (params.featured) {
          filters.isPublished = true;
        }
        if (params.streams) {
          filters.isStream = true;
        }
        if (params.search) {
          filters.title = {
            $containsi: params.search,
          };
        }
        if (params.date?.from && params.date?.to) {
          filters.$and = [
            { starts: { $gte: params.date.from } },
            { ends: { $lte: params.date.to } },
          ];
        } else if (params.date?.from) {
          filters.starts = { $gte: params.date.from };
        } else if (params.date?.to) {
          filters.ends = { $lte: params.date.to };
        }
        if (params.types?.length) {
          filters.aa_events_type = { id: { $in: params.types } };
        }

        console.log("filters", filters);

        const response = await find("aa-events", {
          populate: ["aa_category", "aa_tags", "aa_events_type", "cover"],
          sort: [params.sort || "createdAt:desc"],
          filters,
          pagination: {
            page: params.page || 1,
            pageSize: params.pageSize || 250,
          },
        });

        this.events = response.data;
        return response;
      } catch (error) {
        this.error = error;
        return {
          data: [],
          meta: {
            pagination: { page: 1, pageSize: 25, pageCount: 1, total: 0 },
          },
        };
      } finally {
        this.loading = false;
      }
    },

    async getCategories() {
      try {
        this.loading = true;
        const { find } = useStrapi();
        const response = await find("aa-categories", {
          populate: ["icon"],
          pagination: { pageSize: 10 }, // Fetch all categories at once
        });
        this.categories = response.data;
        return response.data;
      } catch (error) {
        this.error = error;
        return [];
      } finally {
        this.loading = false;
      }
    },

    async getTags() {
      try {
        this.loading = true;
        const { find } = useStrapi();
        const response = await find("aa-tags", {
          pagination: { pageSize: 100 }, // Fetch all tags at once
          sort: ["title:asc"], // Sort tags alphabetically A-Z
        });
        this.tags = response.data;
        return response.data;
      } catch (error) {
        this.error = error;
        return [];
      } finally {
        this.loading = false;
      }
    },
    async getEventTypes() {
      try {
        this.loading = true;
        const { find } = useStrapi();
        const response = await find("aa-events-types", {
          pagination: { pageSize: 50 },
          sort: ["title:asc"],
        });
        this.eventTypes = response.data;
        return response.data;
      } catch (error) {
        this.error = error;
        return [];
      } finally {
        this.loading = false;
      }
    },

    async getRecommendedEvents(params: {
      categoryId?: number;
      tagId?: number;
      typeId?: number; // Add typeId parameter
      currentEventId: number;
      page?: number;
    }) {
      try {
        this.loading = true;
        const { find } = useStrapi();

        const filters: any = {
          id: { $ne: params.currentEventId },
        };

        if (params.categoryId) {
          filters.aa_category = { id: { $eq: params.categoryId } };
        }
        if (params.tagId) {
          filters.aa_tags = { id: { $eq: params.tagId } };
        }
        if (params.typeId) {
          filters.aa_events_type = { id: { $eq: params.typeId } };
        }

        const response = await find("aa-events", {
          populate: ["aa_category", "aa_tags", "aa_events_type", "cover"],
          filters,
          pagination: {
            page: params.page || 1,
            pageSize: 4,
          },
          sort: ["starts:desc"],
        });

        if (params.page && params.page > 1) {
          this.recommendedEvents = [
            ...this.recommendedEvents,
            ...response.data,
          ];
        } else {
          this.recommendedEvents = response.data;
        }
        this.pagination = response.meta.pagination;
        return response;
      } catch (error) {
        this.error = error;
        return [];
      } finally {
        this.loading = false;
      }
    },
  },
});
